import * as Sentry from '@sentry/react';

/**
 * Sentry Configuration for Chart Your Course AI
 * 
 * This file initializes Sentry for error tracking and monitoring.
 * Replace the DSN with your actual Sentry project DSN.
 */

// Check if we're on the production domain
const isProductionDomain = () => {
  return typeof window !== 'undefined' && window.location.hostname === 'trycarto.ai';
};

// Check if analytics should be disabled
const isAnalyticsDisabled = () => {
  return typeof window !== 'undefined' && localStorage.getItem('analytics_off') === 'true';
};

// Initialize Sentry
export const initSentry = () => {
  // Don't initialize if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log('[ANALYTICS DISABLED] Sentry disabled - analytics_off is set');
    return;
  }

  // Only initialize Sentry on production domain
  if (!isProductionDomain()) {
    console.log('[DEV] Sentry disabled - not on trycarto.ai domain');
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || "https://146542dcb828d75f39373598fda92032@o4509408389431296.ingest.us.sentry.io/4509408409944069",
    environment: import.meta.env.MODE,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions
    // Session Replay - capture ALL sessions since traffic is low
    replaysSessionSampleRate: 1.0, // Capture 100% of sessions for full visibility
    replaysOnErrorSampleRate: 1.0, // Capture 100% when errors occur
    
    beforeSend(event) {
      // Don't send events if analytics are disabled
      if (isAnalyticsDisabled()) {
        console.log('[ANALYTICS DISABLED] Sentry event blocked - analytics_off is set:', event);
        return null;
      }

      // Don't send events in development unless explicitly enabled
      if (import.meta.env.MODE === 'development' && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.warn('Sentry event blocked in development:', event);
        return null;
      }
      return event;
    },
  });
};

/**
 * Enhanced error logging with Sentry integration
 * Replaces the basic console logging with proper error tracking
 */
export const logError = (context: string, error: any, additionalData?: any) => {
  // Still log to console for immediate debugging
  console.group(`🚨 Error in ${context}`);
  console.error('Error object:', error);
  if (additionalData) {
    console.error('Additional context:', additionalData);
  }
  console.error('Stack trace:', error?.stack);
  console.groupEnd();

  // Don't send to Sentry if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log('[ANALYTICS DISABLED] Would send error to Sentry:', { context, error, additionalData });
    return;
  }

  // Only send to Sentry if on production domain
  if (!isProductionDomain()) {
    console.log('[DEV] Would send error to Sentry:', { context, error, additionalData });
    return;
  }

  // Send to Sentry with context
  Sentry.withScope((scope) => {
    scope.setTag('context', context);
    scope.setLevel('error');
    
    if (additionalData) {
      scope.setContext('additional_data', additionalData);
    }

    // Special handling for email submission errors
    if (context.includes('submitEmailToSupabase')) {
      scope.setTag('error_type', 'email_submission');
      scope.setTag('form_source', additionalData?.formSource || 'unknown');
      
      // Add user context without exposing sensitive data
      if (additionalData?.email) {
        scope.setUser({
          email: additionalData.email,
        });
      }
      
      // Add extra context for different error types
      if (context.includes('API Error')) {
        scope.setTag('error_subtype', 'api_error');
        scope.setContext('api_response', {
          status: additionalData?.status,
          errorData: additionalData?.errorData,
        });
      } else if (context.includes('Function Error')) {
        scope.setTag('error_subtype', 'function_error');
        scope.setContext('function_response', additionalData?.result);
      } else if (context.includes('Catch Block')) {
        scope.setTag('error_subtype', 'network_error');
        scope.setContext('error_details', {
          errorType: additionalData?.errorType,
          errorName: additionalData?.errorName,
        });
      }
    }

    // Send the error to Sentry
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(`${context}: ${String(error)}`, 'error');
    }
  });
};

/**
 * Log email submission success for monitoring
 */
export const logEmailSubmissionSuccess = (formSource: string, email: string) => {
  // Don't send to Sentry if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log('[ANALYTICS DISABLED] Would log email submission success to Sentry:', { formSource, email });
    return;
  }

  if (!isProductionDomain()) {
    console.log('[DEV] Would log email submission success to Sentry:', { formSource, email });
    return;
  }

  Sentry.addBreadcrumb({
    message: 'Email submission successful',
    category: 'email',
    level: 'info',
    data: {
      form_source: formSource,
      email: email,
    },
  });
};

/**
 * Log email submission attempt for tracking
 */
export const logEmailSubmissionAttempt = (formSource: string, email: string) => {
  // Don't send to Sentry if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log('[ANALYTICS DISABLED] Would log email submission attempt to Sentry:', { formSource, email });
    return;
  }

  if (!isProductionDomain()) {
    console.log('[DEV] Would log email submission attempt to Sentry:', { formSource, email });
    return;
  }

  Sentry.addBreadcrumb({
    message: 'Email submission attempted',
    category: 'email',
    level: 'info',
    data: {
      form_source: formSource,
      email: email,
    },
  });
};

/**
 * Set user context for better error tracking
 */
export const setSentryUser = (email: string, additionalData?: Record<string, any>) => {
  // Don't send to Sentry if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log('[ANALYTICS DISABLED] Would set Sentry user:', { email, additionalData });
    return;
  }

  if (!isProductionDomain()) {
    console.log('[DEV] Would set Sentry user:', { email, additionalData });
    return;
  }

  Sentry.setUser({
    email,
    ...additionalData,
  });
};

/**
 * Add custom breadcrumb for tracking user actions
 */
export const addSentryBreadcrumb = (message: string, category: string, data?: Record<string, any>) => {
  // Don't send to Sentry if analytics are disabled
  if (isAnalyticsDisabled()) {
    console.log('[ANALYTICS DISABLED] Would add Sentry breadcrumb:', { message, category, data });
    return;
  }

  if (!isProductionDomain()) {
    console.log('[DEV] Would add Sentry breadcrumb:', { message, category, data });
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
};

export default Sentry; 